/**
 * @file: whiteHeaderStackOptions.tsx
 *
 * The clean white header chrome shared by the message-side stacks (the inbox
 * and the app-level chat flow): a white bar with a hairline bottom border (no
 * drop shadow), a centred bold title, and the app back icon.
 *
 * It lives here — app navigation, not inside either flow — because the chat
 * flow is pushed on the ROOT stack while the inbox lives in a tab: neither can
 * own the other's chrome, and a copy in each would drift.
 */

import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { MultilineHeaderTitle } from '@euler/components/MultilineHeaderTitle';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  StackNavigationOptions,
  TransitionPresets,
} from '@react-navigation/stack';
import { useMemo } from 'react';
import { StyleSheet } from 'react-native';

export const useWhiteHeaderStackOptions = (): StackNavigationOptions => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () => ({
      ...TransitionPresets.SlideFromRightIOS,
      headerMode: 'float',
      headerTitleAlign: 'center',
      headerBackTitle: ' ',
      headerStyle: {
        backgroundColor: '#fff',
        // A subtle bottom hairline under the header; drop the Android
        // elevation + iOS shadow so only the border shows (no drop-shadow
        // over the page).
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: '#f2f2f2',
        elevation: 0,
        shadowOpacity: 0,
      },
      headerTintColor: '#111',
      headerTitleStyle: {
        fontSize: rpx(18),
        fontFamily: FontFamily.NotoSans.Bold,
        color: '#111',
      },
      cardStyle: {
        flex: 1,
        backgroundColor: '#f7f7f7',
      },
      headerTitle: MultilineHeaderTitle,
      headerBackImage: () => <BackIcon fill="#111" />,
    }),
    [rpx],
  );
};
